import type { intelligence } from '@cwos/api-client';
import { sourcePhrase } from './toolPhrases.js';

/**
 * "Based on Phibian records" — the turn's provenance, translated into product
 * language.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE REPLACED, AND WHY.
 *
 * The first surface rendered the manifest in the runtime's own vocabulary:
 * "What this is based on — 2 retrievals", then a nested "Developer detail"
 * listing `analytics.query_metric@2 · 412 time_sessions · hash 1a2b…`. Every
 * one of those tokens is a fact about the IMPLEMENTATION. "Retrieval" is how
 * the tool loop counts itself; `@2` is the manifest's occurrence ordinal;
 * `time_session` is a table; the hash exists so a manifest can be verified
 * without storing rows. A person asking how many hours a program delivered is
 * owed none of it, and shipping it taught them the surface was built for
 * somebody else.
 *
 * So the detailed provenance stays exactly where it was — on the wire, in
 * `response.evidence`, unchanged and complete. What changes is that the UI
 * TRANSLATES it instead of transcribing it.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE TWO FIREWALLS, both of which exist because a mapping table is always one
 * server release out of date.
 *
 *   1. NO RESOURCE TYPE REACHES A READER. `time_session` maps to "time
 *      records"; an UNMAPPED type maps to the neutral "records", never to the
 *      raw key. The types that would leak worst are precisely the ones no
 *      product word covers — `intelligence_tool_call`, `intelligence_memory_item`,
 *      `eval` — so the fallback has to be neutral rather than "reasonable".
 *   2. NO TOOL ID REACHES A READER. The area label comes from `sourcePhrase`,
 *      which is guarded in `toolPhrases.ts` against ever returning its own id.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT IT WILL AND WILL NOT SAY.
 *
 * Only what the response actually carries. `ToolEvidence` has `accessed`
 * counts, a scope of UUIDs, a retrieval clock and a content hash — and NO
 * period. The period a reader wants ("Aug 17–23") lives on the analytics
 * RESULT (`AnalyticsKpi.period` / `periodLabel`), which is the server's
 * resolved window, so it is read from there and attached only to the analytics
 * line it actually describes. It is never derived from `atMs`: the clock at
 * retrieval time is when the question was asked, not what the figure covers,
 * and printing one as the other would be a fabricated date on an audit line.
 *
 * The scope UUIDs, the content hash and the retrieval clock are not rendered
 * at all. They are verification material for a manifest, not information for a
 * person, and none of them has a product sentence that is worth its noise.
 */

/**
 * Resource type → the words a person uses, singular and plural.
 *
 * Keys are the `resourceType` values the API actually emits today (the tool
 * families under `domains/intelligence/tools/*` plus the analytics engine).
 * Anything absent falls to {@link NEUTRAL_RESOURCE} rather than to itself.
 */
const RESOURCE_WORDS: Readonly<Record<string, readonly [string, string]>> = {
  worker: ['worker', 'workers'],
  client: ['client', 'clients'],
  time_session: ['time record', 'time records'],
  supervision: ['supervision record', 'supervision records'],
  knowledge_article: ['policy', 'policies'],
  metric_catalog: ['metric definition', 'metric definitions'],
  dashboard_layout: ['dashboard', 'dashboards'],
  workflow_rule: ['automation', 'automations'],
  rule_catalog: ['automation rule', 'automation rules'],
  integration: ['integration', 'integrations'],
  message: ['message', 'messages'],
  doctrine_document: ['guidance document', 'guidance documents'],
  doctrine_principle: ['guidance principle', 'guidance principles'],
  rows: ['record', 'records'],
};

/**
 * The honest degradation for a resource type this build has no word for.
 *
 * "Records" is true of everything the manifest can carry and specific about
 * nothing, which is exactly right for a type the screen does not recognize.
 */
const NEUTRAL_RESOURCE: readonly [string, string] = ['record', 'records'];

/**
 * The key-leak guard for resource words, applied on the way out.
 *
 * The failure it exists to catch is a `?? resourceType` fallback, which ships
 * a column name to a reader: `time_session`, `intelligence_tool_call`,
 * `dashboard_layout`.
 *
 * IT ONLY FIRES ON COMPOUND KEYS, and that is deliberate rather than a gap. A
 * single lowercase token like `worker`, `client` or `message` is ALREADY the
 * English word for the thing — "32 workers" is the correct sentence, and a
 * guard that rejected it for resembling its own key would replace a good word
 * with the neutral one and call that safety. Compound keys are where a leak is
 * both possible and unmistakable, because no product phrase contains an
 * underscore, a dot or a camelCase seam.
 *
 * Matching is case- and separator-insensitive, because `time_session` and
 * `timeSession` are one leak wearing two hats.
 */
function leaksType(word: string, resourceType: string): boolean {
  const compound = /[._-]/.test(resourceType) || /[a-z][A-Z]/.test(resourceType);
  if (!compound) return false;
  const flatten = (value: string): string => value.toLowerCase().replace(/[._\s-]/g, '');
  return flatten(word).includes(flatten(resourceType));
}

/** The words for one resource type, never the type itself. */
export function resourceWords(resourceType: string): readonly [string, string] {
  const known = Object.prototype.hasOwnProperty.call(RESOURCE_WORDS, resourceType)
    ? RESOURCE_WORDS[resourceType]
    : undefined;
  if (known === undefined) return NEUTRAL_RESOURCE;
  if (leaksType(known[0], resourceType) || leaksType(known[1], resourceType)) {
    return NEUTRAL_RESOURCE;
  }
  return known;
}

/** "32 workers", "1 policy" — a count and the right noun for it. */
export function countPhrase(resourceType: string, count: number): string {
  const [one, many] = resourceWords(resourceType);
  return `${count} ${count === 1 ? one : many}`;
}

// ---------------------------------------------------------------------------
// The resolved period, from the result that actually has one.
// ---------------------------------------------------------------------------

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

interface CalendarDay {
  year: number;
  month: number;
  day: number;
}

/**
 * A `YYYY-MM-DD` boundary as its calendar parts, or null.
 *
 * Parsed by hand rather than through `Date`: `new Date('2026-08-17')` is UTC
 * midnight, which renders as the 16th anywhere west of Greenwich. A period
 * boundary the server resolved must not shift by a day because of where the
 * reader is sitting.
 */
function calendarDay(iso: string): CalendarDay | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/**
 * "Aug 17–23", "Aug 28 – Sep 3", "Dec 30, 2025 – Jan 5, 2026".
 *
 * Deterministic and locale-independent by construction — no `toLocaleString`,
 * because the same evidence line must read the same in every test and every
 * timezone. Returns null when either boundary is not a date this can trust,
 * which renders as no period line rather than as a guess.
 */
export function formatPeriod(from: string, to: string): string | null {
  const start = calendarDay(from);
  const end = calendarDay(to);
  if (start === null || end === null) return null;
  const startMonth = MONTHS[start.month - 1];
  const endMonth = MONTHS[end.month - 1];
  if (start.year !== end.year) {
    return `${startMonth} ${start.day}, ${start.year} – ${endMonth} ${end.day}, ${end.year}`;
  }
  if (start.month !== end.month) {
    return `${startMonth} ${start.day} – ${endMonth} ${end.day}`;
  }
  if (start.day === end.day) return `${startMonth} ${start.day}`;
  return `${startMonth} ${start.day}–${end.day}`;
}

/** What an analytics answer resolved to, when the turn carried one. */
interface AnalyticsFacts {
  /** The metric's own friendly label ("Hours worked") — never its catalog key. */
  readonly label: string | undefined;
  /** The resolved window, already in words. */
  readonly period: string | undefined;
  /** "32 workers", when the executor reported the population it counted. */
  readonly subjects: string | undefined;
}

/**
 * The analytics facts a turn carries, read from the typed result payload.
 *
 * `AnalyticsKpi` is the only block that resolves a period and a population, and
 * it carries BOTH a catalog key (`metric`, e.g. `hours_worked`) and a human
 * label (`label`, e.g. "Hours worked"). Only the label is read here. The key is
 * the metric's identity in the catalog and has no business in a sentence — it
 * is the exact "internal metric key where a friendly label exists" the makeover
 * names.
 */
export function analyticsFacts(
  results: intelligence.TurnResponse['results'],
): AnalyticsFacts | null {
  if (results === undefined) return null;
  for (const block of results) {
    if (block.kind !== 'kpi') continue;
    const period =
      block.periodLabel ?? formatPeriod(block.period.from, block.period.to) ?? undefined;
    const subjects =
      block.subjectCount !== undefined
        ? `${block.subjectCount} ${block.subjectNoun ?? (block.subjectCount === 1 ? 'worker' : 'workers')}`
        : undefined;
    return { label: block.label, period, subjects };
  }
  return null;
}

// ---------------------------------------------------------------------------
// The lines themselves.
// ---------------------------------------------------------------------------

/** One line under "Based on Phibian records", already in product language. */
export interface EvidenceLine {
  /** Stable render key. Positional — the manifest's order is the server's. */
  readonly key: string;
  /**
   * THE CITATION NUMBER, 1-based, in manifest order.
   *
   * This is the whole reason a per-claim citation can be rendered at all. The
   * explanation's `supportingEvidence` and every `evidenceRef` on it are
   * server-issued `ev_…` ids — verification material, and exactly the kind of
   * token `evidenceView.ts` exists to keep off a screen. A NUMBER is the same
   * pointer with none of the payload: it says which of the retrievals below a
   * sentence rests on, and it says nothing about the implementation.
   *
   * Positional rather than derived from the id, so the marker beside a sentence
   * and the line it points at cannot disagree — they are one ordering, the
   * server's.
   */
  readonly citation: number;
  /** The words. Never a tool id, a resource type, an ordinal or a hash. */
  readonly text: string;
}

/**
 * `ev_…` → the citation number of the manifest line that carries it.
 *
 * WHY IT IS A LOOKUP AND NOT A RENDER. `supportingEvidence` and the per-delta
 * and per-factor `evidenceRef`s are the §24 element that links a claim to its
 * source, and they were rendered NOWHERE: the panel described the turn's
 * provenance as prose ("Workforce records · 32 workers") and no sentence was
 * attached to any of it. A per-claim citation nothing renders is a per-claim
 * citation that does not exist.
 *
 * ENTRIES WITHOUT AN `evidenceId` ARE ABSENT FROM THE MAP AND STILL NUMBERED IN
 * THE LIST. The wire field is optional — a failure envelope from an older
 * server may predate issuance — and such an entry is, in the schema's own
 * words, "simply not citable". It still consumes a number, because the numbers
 * are the reader's index into the list they can see, and skipping one would
 * make the list jump.
 *
 * A ref that is not here is NOT SILENTLY DROPPED by the callers: the composer
 * rejects the whole envelope for an unresolvable ref, so a ref arriving here
 * without a home means this response's explanation and its manifest disagree.
 * That is a defect worth a reader seeing, not one worth hiding, and
 * `citationsFor` renders it as an unresolved marker rather than as nothing.
 */
export function citationIndex(
  evidence: readonly intelligence.ToolEvidence[],
): ReadonlyMap<string, number> {
  const index = new Map<string, number>();
  evidence.forEach((entry, position) => {
    if (entry.evidenceId === undefined) return;
    // FIRST WINS. Two manifest entries cannot share a server-issued id, but if
    // one ever did, a claim must point at one line rather than at whichever was
    // written last.
    if (!index.has(entry.evidenceId)) index.set(entry.evidenceId, position + 1);
  });
  return index;
}

/**
 * True when this tool's evidence is the analytics answer's own — the one entry
 * the resolved period and population describe.
 */
function isAnalyticsTool(toolId: string): boolean {
  return toolId === 'analytics.query_metric';
}

/**
 * The turn's provenance as lines a person can read.
 *
 * One line per manifest entry, in manifest order, built from what the entry
 * genuinely carries:
 *
 *   `<area> · <counts> · <period>`
 *
 * The area is the tool's plain-language source ("Workforce records"), except on
 * the analytics entry, where the metric's own label is more specific and more
 * useful ("Hours worked"). The counts are the entry's `accessed` list with
 * every resource type translated. The period is attached ONLY to the analytics
 * entry, because it is the analytics result's resolved window and says nothing
 * true about a roster read.
 *
 * An entry that accessed nothing still renders — a retrieval that came back
 * empty is a fact about the answer, and dropping it would quietly shrink the
 * provenance. It says so in words ("nothing found") rather than as "0 records",
 * which reads as a measurement.
 */
export function evidenceLines(
  evidence: readonly intelligence.ToolEvidence[],
  results: intelligence.TurnResponse['results'],
): EvidenceLine[] {
  const analytics = analyticsFacts(results);
  return evidence.map((entry, index) => {
    const forAnalytics = isAnalyticsTool(entry.toolId);
    const parts: string[] = [];

    const area =
      forAnalytics && analytics?.label !== undefined ? analytics.label : sourcePhrase(entry.toolId);
    parts.push(area);

    const counts = entry.accessed
      .filter((accessed) => accessed.count > 0)
      .map((accessed) => countPhrase(accessed.resourceType, accessed.count));
    if (counts.length > 0) {
      parts.push(...counts);
    } else if (forAnalytics && analytics?.subjects !== undefined) {
      // The executor counted a population even where the manifest recorded no
      // per-type rows; that count is the honest subject line for this entry.
      parts.push(analytics.subjects);
    } else {
      parts.push('nothing found');
    }

    if (forAnalytics && analytics?.period !== undefined) parts.push(analytics.period);

    return { key: `${index}`, citation: index + 1, text: parts.join(' · ') };
  });
}
